/**
 * Lane C0-FACE: the game's face layer (user decisions 2026-10-10, live in render/rig/sets.ts `appearanceOf`): NPC faces picked by the NPC's id (no same-face clones), expressions following the pose everywhere (attack →
 * shout, hurt / blockHit / fall → hurt, block / cast → angry, eyes closed → blink; draw.ts `exprOf`).
 *
 * The person's id travels on the look (`Look.faceId`, set for every look in looks/index.ts), so dressed copies,
 * workers and the frame cache all see it. Faces are drawn into the cached rig frames, so there is no per-frame cost.
 * Rollback: render/rig/figure.ts `faceLayer` (the old face) is kept; swap it back in sets.ts.
 */
import type { Look } from '../../sprites';
import type { Layer } from '../figure';
import { labFaceLayer } from './draw';
import { faceFor } from './pick';

/**
 * Which set young and adult people get: `auto` = the game's old face sharpened (elders the new set), `elderStyle` = the
 * elders' style without age cues (the user's pick, 2026-10-10).
 */
export const YOUNG_STYLE: 'auto' | 'elderStyle' = 'elderStyle';

/** The face layer for a person: picked by the look's `faceId` (the player keeps its own), expression from the pose. */
export function gameFaceLayer(look: Look): Layer {
  // A look made outside LOOKS (no id) is keyed on its colours, as the old face was.
  const id = look.faceId ?? `${look.skin}|${look.hair}|${look.hairStyle}|${look.eye}|${look.female ? 1 : 0}`;
  const face = faceFor(id, look, 'auto', 'id', YOUNG_STYLE);
  return labFaceLayer(face, { skin: look.skin, skinS: look.skinS }, look.hair, look.beardColor);
}
